import { mapActions, mapGetters } from 'vuex';

/** Parts a cards view defines for the mixin; checked at creation. */
const REQUIRED_VIEW_PARTS = ['reportPath', 'resolveCardTarget', 'toCardInput'];

/**
 * Card navigation and saving of a protocol's cards view (see feature page projekt-protokolle):
 * "weiter"/"zurück" opens the target card and saves the source card, the card state toggle saves
 * at once. With `saveCard` (data block of the protocol tab) only the card input is saved, without
 * it (deprecated mobile layout) the whole project.
 *
 * The view defines:
 * - `reportPath`: subdocument of `project` holding the cards, e.g. 'measurement';
 * - `cards`: the card states, with `navigateTo: this.goToCard` and `completed: this.completedState`;
 * - `resolveCardTarget(target)`: the card name of a navigation target (branch targets);
 * - `toCardInput(report, fieldPaths)`: the card input of the protocol for the given fields;
 * - optionally `extraReferenceData`: reference data the view adds for its cards.
 */
export const ReportCardsMixin = {
  props: {
    /**
     * Saves one card (data block of the protocol), called with the sparse card input. Without it
     * (deprecated mobile layout) "weiter" saves the whole project as before.
     */
    saveCard: { type: Function, default: null },
    /** Reference data of the block; without them the store getter is used. */
    reportTemplateImages: { type: Array, default: null },
  },
  computed: {
    ...mapGetters(['getReportTemplateImages']),
    referenceData() {
      return {
        reportTemplateImages: this.reportTemplateImages ?? this.getReportTemplateImages,
        ...this.extraReferenceData,
      };
    },
    /**
     * The report subdocument, read on every access: views create it after mount by plain
     * assignment, which a cached computed would not see.
     */
    report: {
      get() {
        return this.project[this.reportPath];
      },
      cache: false,
    },
  },
  created() {
    const missingPart = REQUIRED_VIEW_PARTS.find((part) => this[part] === undefined);
    if (missingPart) {
      throw new Error(`ReportCardsMixin: the view must define ${missingPart}`);
    }
  },
  methods: {
    ...mapActions(['uploadProjectAttachment', 'updateProject']),

    /**
     * Save and open the target card: with `saveCard` only the fields of the source card (named by
     * `sourceCard`), else the whole project.
     */
    async goToCard(target, update = true, sourceCard = null) {
      const card = this.resolveCardTarget(target);

      this.toggle(this.cards[card]);

      this.$nextTick(() => {
        this.scrollTo(card);
      });

      if (!update) {
        return;
      }
      if (this.saveCard) {
        await this.saveCard(this.toCardInput(this.report, this.fieldsOfCard(sourceCard)));
        return;
      }
      await this.updateProject(this.project);
    },

    /** Field paths of the card with `name`; none for an unknown card. */
    fieldsOfCard(name) {
      const card = Object.values(this.cards).find((entry) => entry && entry.name === name);
      return card ? card.fields : [];
    },

    /** State of the card `type`, registered as open when missing; a new value saves at once. */
    completedState(type, newValue = null) {
      this.report.completed = this.report.completed ? this.report.completed : [];

      const existingField = this.report.completed.find((field) => field.type === type);

      if (!existingField) {
        const newField = { type: type, value: false };

        this.report.completed.push(newField);
        return newField.value;
      } else if (newValue !== null) {
        existingField.value = newValue;
        // The toggle saves at once with the block (see feature page projekt-protokolle).
        if (this.saveCard) {
          this.saveCard(this.toCardInput(this.report, []));
        }
      }

      return existingField.value;
    },

    toggle(item) {
      item.opened = !item.opened;
    },

    /** Emit a scroll event, the AccordionCard Component will resolve it. */
    scrollTo(name) {
      this.$eventHub.$emit('scroll-to-accordion', name);
    },

    async onUploadProjectAttachment(payload) {
      await this.uploadProjectAttachment(payload);
    },
  },
};
